import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { callActions as nucleusCallActions, moderatorActions } from '@lifesize/nucleus';
import { hooks } from '@lifesize/clients.sdk';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as MoreIcon } from '@lifesize/ux-assets/vectors/svgIcons/more.svg';
import { ReactComponent as HandIcon } from '@lifesize/ux-assets/vectors/svgIcons/hand.svg';
import { ReactComponent as EndCallIcon } from '@lifesize/ux-assets/vectors/svgIcons/end-call.svg';
import { ReactComponent as PresentIcon } from '@lifesize/ux-assets/vectors/svgIcons/share-screen.svg';
import { fade, makeStyles, Theme } from '@material-ui/core';
import DropdownComp from 'components/Dropdown/Dropdown';
import { getMicConfig } from 'components/InCall/InCallParticipantList/ParticipantButtons/ParticipantMicButton';
import Modals from 'constants/Modals';
import { openInCallModal } from 'actions/InCallModal';

interface Props {
  participant: any;
}

const ParticipantMoreButton = ({ participant }: Props) => {
  const classes = useStyles({});
  const userIsModerator = hooks.useIsCurrentUserModerator();
  const { handRaised, isPresenting, isSelf } = participant;
  const dispatch = useDispatch();
  const conferenceInfo = hooks.useConferenceInfo();
  const canBeMuted = userIsModerator && conferenceInfo?.state === 'escalated';
  const micConfig = getMicConfig(participant, userIsModerator, classes);

  const handleLowerHandClicked = (participantId) => {
    dispatch(moderatorActions.lowerParticipantHand(participantId));
  };

  const handleMuteParticipantClicked = (participant) => {
    dispatch(nucleusCallActions.muteParticipant(participant, !participant.serverAudioMuteState));
  };

  const handleRemoveFromMeetingClicked = (participant) => {
    dispatch(openInCallModal(Modals.removeFromMeeting, { participant }));
  };

  const handleStopPresenterClicked = (participant) => {
    dispatch(openInCallModal(Modals.stopPresenter, { participant }));
  };

  const CurrentIcon = micConfig.menuOptionIcon;

  if (userIsModerator && !isSelf) {
    return (
      <DropdownComp
        menuNoLeft
        menuNoTop
        buttonClassName={classes.dropdownButton}
        buttonComponent={
          <div className={`${classes.button} ${classes.lastButton}`} data-testid="moreDropdownMenuTrigger">
            <MoreIcon className={classes.buttonIcon} data-testid="moreIcon" />
          </div>
        }
        // containerClassName={classes.dropdown}
        openButtonComponent={
          <div className={`${classes.button} ${classes.lastButton}`} data-testid="moreDropdownMenuTrigger">
            <MoreIcon className={classes.buttonIcon} data-testid="moreIcon" />
          </div>
        }
        dropdownClassName={classes.dropdownMenu}
        menuComponent={
          <ul>
            {!!handRaised && (
              <li
                data-testid="moreMenuOptionLowerHand"
                onClick={() => handleLowerHandClicked(participant.participantId)}
              >
                <HandIcon className={classes.buttonIcon} />
                <span>
                  <FormattedMessage id="components.inCall.participantDrawer.lowerHand" defaultMessage="Lower Hand" />
                </span>
              </li>
            )}
            {!!canBeMuted && (
              <li
                data-testid={micConfig.menuOptionDataTestId}
                onClick={() => handleMuteParticipantClicked(participant)}
              >
                <CurrentIcon className={classes.buttonIcon} />
                <span>{micConfig.menuOptionLabel}</span>
              </li>
            )}
            <li
              data-testid="moreMenuOptionRemoveFromMeeting"
              onClick={() => handleRemoveFromMeetingClicked(participant)}
            >
              <EndCallIcon className={classes.buttonIcon} />
              <span>
                <FormattedMessage
                  id="components.inCall.participantDrawer.removeFromMeeting"
                  defaultMessage="Remove from Meeting"
                />
              </span>
            </li>
            {!!isPresenting && (
              <li data-testid="moreMenuOptionEndPresentation" onClick={() => handleStopPresenterClicked(participant)}>
                <PresentIcon className={classes.buttonIcon} />
                <span>
                  <FormattedMessage
                    id="components.inCall.participantDrawer.endPresentation"
                    defaultMessage="End Presentation"
                  />
                </span>
              </li>
            )}
          </ul>
        }
      />
    );
  }
  // non-moderators see nothing (blank space)
  return <div className={`${classes.button} ${classes.noButton} ${classes.lastButton}`} />;
};

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    alignItems: 'center',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    fontSize: '1em', // prevents global button default
    height: '100%', // necessary for the drop-down button
    justifyContent: 'center',
    margin: '0 3px',
    outline: 'none',
    padding: 0,
    position: 'relative',
    width: '2em'
  },
  noButton: {
    cursor: 'default'
  },
  lastButton: {
    marginRight: 0
  },
  buttonIcon: {
    height: '2em',
    width: 'auto'
  },
  dropdownButton: {
    height: '100%'
  },
  dropdownMenu: {
    right: 0,
    top: 'calc(100% + 5px)',
    width: 'auto',
    '& ul': {
      margin: `6px 0`,
      padding: 0,
      '& li': {
        // menu option
        alignItems: 'center',
        cursor: 'pointer',
        display: 'flex',
        color: colors.smoke,
        padding: `0 4px`,
        listStyle: 'none',
        '& svg': {
          color: colors.squadronBlue
        },
        '& span': {
          flex: '1 0 auto',
          marginLeft: theme.spacing(1),
          maxWidth: 170,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        },
        '&:hover': {
          backgroundColor: fade(colors.squadronBlue, 0.1)
        }
      }
    },
    '&:before': {
      right: 3
    },
    '&:after': {
      right: 3
    }
  }
}));

export default ParticipantMoreButton;
